/**
 * Sankat Mochan — Paid 10-min AT Session
 * 200 AT debit · 33 AT 7-level webchain split · OpenAI TTS · Avatar auto-stats
 * Standalone immersive layout (no app chrome).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Send, X, Sparkles, Clock, Volume2, ShieldCheck, ArrowLeft, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import BuyATBalanceModal from '../../components/BuyATBalanceModal';

const SankatPaidSession = () => {
  const [avatars, setAvatars] = useState([]);
  const [pricing, setPricing] = useState({ cost_at: 200, duration_sec: 900, warning_at_sec: 840, auto_delete_hours: 48 });
  const [session, setSession] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [warned, setWarned] = useState(false);
  const [showBuyAT, setShowBuyAT] = useState(false);
  const [listening, setListening] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  const audioRef = useRef(null);
  const recogRef = useRef(null);
  const navigate = useNavigate();

  const loadSaved = () => {
    api.get('/sankat-mochan/paid-session/saved')
      .then((r) => setSavedSessions(r.data.sessions || []))
      .catch(() => {});
  };

  useEffect(() => {
    api.get('/sankat-mochan/avatars').then((r) => setAvatars(r.data.avatars || [])).catch(() => {});
    api.get('/sankat-mochan/paid-session/pricing').then((r) => setPricing(r.data)).catch(() => {});
    loadSaved();
  }, []);

  useEffect(() => {
    if (!session) return;
    const tick = setInterval(() => {
      setRemaining((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 60 && !warned) {
          setWarned(true);
          toast.warning('⚠️ 1 minute remaining. Session jaldi khatm hone wala hai.', { duration: 10000 });
        }
        if (next === 0) {
          // Instead of hard-ending immediately, open the Continue/Exit modal.
          setShowExpiredModal(true);
          clearInterval(tick);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [session, warned]);

  const continueSession = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await api.post('/sankat-mochan/paid-session/continue', { session_id: session.id });
      setRemaining(r.data.remaining_sec || pricing.duration_sec || 900);
      setShowExpiredModal(false);
      setWarned(false);
      toast.success(`Extended by ${Math.floor((r.data.duration_sec || 900) / 60)} min · ${pricing.cost_at} AT deducted`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not extend session';
      toast.error(detail);
      if (String(detail).toLowerCase().includes('insufficient')) setShowBuyAT(true);
    } finally {
      setBusy(false);
    }
  };

  const start = async (a) => {
    setBusy(true);
    try {
      const r = await api.post('/sankat-mochan/paid-session/start', { avatar_id: a.id });
      setSession({ id: r.data.session_id, ...r.data });
      setAvatar(r.data.avatar || a);
      setRemaining(r.data.duration_sec || 900);
      const mins = Math.floor((r.data.duration_sec || 900) / 60);
      setMessages([{
        role: 'ai',
        text: `🙏 नमस्कार! मैं ${(r.data.avatar || a).display_name} हूँ। आप अपनी समस्या share kijiye, main poori taur se aapke saath hoon. ${pricing.cost_at} AT charge ho gaya hai, ab ${mins} min aap ke saath hain.`,
      }]);
      toast.success(`Connected with ${(r.data.avatar || a).display_name} ✨ · ${pricing.cost_at} AT deducted`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not start session';
      toast.error(detail);
      if (String(detail).toLowerCase().includes('insufficient')) setShowBuyAT(true);
    } finally {
      setBusy(false);
    }
  };

  const endSession = async (reason = 'user') => {
    if (!session) return;
    try { await api.post('/sankat-mochan/paid-session/end', { session_id: session.id }); } catch (_) { /* ignore */ }
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    if (reason === 'expired') toast.info('Session ended. फिर मिलते हैं। 🙏', { duration: 6000 });
    setSession(null); setAvatar(null); setMessages([]); setRemaining(0); setWarned(false);
    setShowExpiredModal(false);
    loadSaved();
  };

  const deleteSaved = async (sid) => {
    try {
      await api.delete(`/sankat-mochan/paid-session/${sid}`);
      setSavedSessions((prev) => prev.filter((s) => s.id !== sid));
      toast.success('Saved session deleted');
    } catch (_) { toast.error('Could not delete'); }
  };

  const fmtCountdown = (sec) => {
    if (!sec && sec !== 0) return '';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const send = async () => {
    if (!input.trim() || !session || busy) return;
    const userText = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: userText }]);
    setBusy(true);
    try {
      const r = await api.post('/sankat-mochan/paid-session/message', { session_id: session.id, text: userText });
      setMessages((m) => [...m, { role: 'ai', text: r.data.reply, tts: r.data.tts_url }]);
      setRemaining(r.data.remaining_sec ?? remaining);
      if (r.data.tts_url) {
        try {
          const blob = await api.get(r.data.tts_url, { responseType: 'blob' });
          const url = URL.createObjectURL(blob.data);
          try { audioRef.current?.pause(); } catch (_) {}
          const audio = new Audio(url); audio.volume = 1.0; audioRef.current = audio;
          audio.play().catch(() => {});
        } catch (_) {}
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally { setBusy(false); }
  };

  const toggleListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input is not supported on this browser'); return; }
    if (listening) { try { recogRef.current?.stop(); } catch (_) {} setListening(false); return; }
    const r = new SR();
    r.lang = 'hi-IN'; r.continuous = false; r.interimResults = false;
    let appended = 0;
    r.onresult = (e) => {
      // Only append new final segments to avoid duplication when Chrome re-fires results.
      let fresh = '';
      for (let i = appended; i < e.results.length; i++) {
        if (e.results[i].isFinal) fresh += e.results[i][0].transcript + ' ';
      }
      appended = e.results.length;
      const txt = fresh.trim();
      if (txt) setInput((prev) => (prev ? prev + ' ' : '') + txt);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch (_) { setListening(false); }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ============ Avatar picker ============
  if (!session) {
    return (
      <div className="min-h-screen pb-16 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="sankat-paid-picker">
        <div className="max-w-6xl mx-auto pt-6">
          <button onClick={() => navigate(-1)} data-testid="sankat-paid-back-btn"
            className="mb-4 inline-flex items-center gap-1.5 text-indigo-200/70 hover:text-amber-300 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="text-center mb-8">
            <ShieldCheck className="w-10 h-10 text-amber-300 mx-auto mb-2" />
            <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 bg-clip-text text-transparent mb-2">
              Sankat Mochan · Paid Consultation
            </h1>
            <p className="text-indigo-100/80 max-w-2xl mx-auto text-sm sm:text-base">
              10 minutes of pure 1-on-1 consultation with your chosen Guru avatar.
              <span className="text-amber-300 font-bold"> {pricing.cost_at} AT</span> ·
              7-level Webchain reward split · personalized guidance with tips & sponsored solutions.
            </p>
          </div>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6 text-xs sm:text-sm text-amber-100 max-w-4xl mx-auto">
            <div className="font-bold text-amber-300 mb-1 inline-flex items-center gap-2">
              <Coins className="w-4 h-4" /> How your {pricing.cost_at} AT is used
            </div>
            <div className="text-amber-100/80">
              33 AT split into 7 Webchain levels [10,7,5,4,3,2,2] AT to your uplines · 167 AT to platform · No free trial.
              <span className="text-emerald-300"> Bina earning ke kharcha nahi.</span>
            </div>
          </div>

          {/* Saved sessions (auto-delete after {pricing.auto_delete_hours} hrs) */}
          {savedSessions.length > 0 && (
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-4 mb-5" data-testid="sankat-saved-sessions">
              <div className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Your Saved Sessions
                <span className="ml-auto text-[10px] text-emerald-200/70 font-normal">
                  Auto-delete in {pricing.auto_delete_hours}h
                </span>
              </div>
              <div className="space-y-2">
                {savedSessions.slice(0, 5).map((s) => (
                  <div key={s.id} className="flex items-center gap-3 rounded-lg bg-slate-900/60 border border-slate-700/50 px-3 py-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="text-amber-200 font-bold truncate">{s.avatar_name || 'Session'}</div>
                      <div className="text-slate-400 truncate">
                        {s.message_count} msgs · {s.status}{s.extensions ? ` · ext×${s.extensions}` : ''}
                      </div>
                    </div>
                    <div className="text-rose-300 font-mono text-[10px] whitespace-nowrap" title="Time until auto-delete">
                      ⏳ {fmtCountdown(s.auto_delete_in_sec)}
                    </div>
                    <button onClick={() => deleteSaved(s.id)} data-testid={`sankat-delete-saved-${s.id}`}
                      title="Delete now"
                      className="w-7 h-7 rounded-md bg-rose-500/20 text-rose-300 hover:bg-rose-500 hover:text-white flex items-center justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
            {avatars.map((a) => (
              <button
                key={a.id}
                onClick={() => start(a)}
                disabled={busy}
                data-testid={`sankat-avatar-${a.id}`}
                className="text-left rounded-2xl overflow-hidden border-2 border-indigo-400/30 bg-slate-900 hover:border-amber-400 hover:scale-[1.02] transition disabled:opacity-50"
              >
                <div className="aspect-square bg-gradient-to-br from-indigo-700 via-purple-700 to-rose-700 overflow-hidden relative">
                  {(a.image_url || a.avatar_url) ? (
                    <img src={a.image_url || a.avatar_url} alt={a.display_name}
                      className="w-full h-full object-cover"
                      style={{ filter: 'saturate(1.15) brightness(1.05)' }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/80 text-6xl font-black">
                      {a.display_name?.[0]}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-wider text-indigo-300">{a.category || 'consultation'}</div>
                  <h3 className="text-base sm:text-lg font-extrabold text-amber-200 mt-1 line-clamp-1">{a.display_name}</h3>
                  <p className="text-xs text-indigo-100/70 mt-1 line-clamp-2">{a.description}</p>
                  <div className="mt-3 text-xs font-bold text-black bg-amber-500 inline-block px-3 py-1.5 rounded-md">
                    Connect · {pricing.cost_at} AT
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button onClick={() => setShowBuyAT(true)} data-testid="sankat-paid-buy-at"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold hover:scale-105 transition inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Buy AT Balance
            </button>
          </div>

          <div className="mt-10 text-center text-xs text-indigo-200/50">
            ⚠️ AI consultation — for guidance only. For medical/legal/financial emergencies, consult a professional.
            If in crisis: iCall +91-9152987821 · Vandrevala 1860-2662-345 (free 24×7).
          </div>
        </div>
        <BuyATBalanceModal open={showBuyAT} onClose={() => setShowBuyAT(false)} />
      </div>
    );
  }

  // ============ Active session ============
  return (
    <div className="min-h-screen pt-3 pb-4 px-3 bg-slate-950 text-white flex flex-col" data-testid="sankat-paid-session">
      <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {(avatar?.image_url || avatar?.avatar_url) ? (
              <img src={avatar.image_url || avatar.avatar_url} alt={avatar?.display_name}
                className="w-10 h-10 rounded-full object-cover border-2 border-amber-400" />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white border-2 border-amber-400 bg-gradient-to-br from-indigo-600 to-rose-600">
                {avatar?.display_name?.[0]}
              </div>
            )}
            <div>
              <div className="font-extrabold text-amber-200 text-sm">{avatar?.display_name}</div>
              <div className="text-[10px] text-slate-400">{avatar?.category}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`text-xs font-mono px-2 py-1 rounded-md ${remaining < 60 ? 'bg-rose-500 text-white anant-pulse-soft' : 'bg-amber-500/20 text-amber-300'}`} data-testid="sankat-paid-timer">
              <Clock className="w-3 h-3 inline mr-1" /> {mmss(remaining)}
            </div>
            {remaining < 180 && remaining > 0 && (
              <button onClick={continueSession} disabled={busy} data-testid="sankat-paid-continue-btn"
                title={`Continue for another ${Math.floor((pricing.duration_sec || 900) / 60)} min (${pricing.cost_at} AT)`}
                className="px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                <Coins className="w-3 h-3" /> +{Math.floor((pricing.duration_sec || 900) / 60)}m
              </button>
            )}
            <button onClick={() => endSession('user')} data-testid="sankat-paid-end-btn"
              className="px-2 py-1 rounded-md bg-rose-500 text-white text-xs font-bold inline-flex items-center gap-1">
              <X className="w-3 h-3" /> End
            </button>
          </div>
        </div>

        {/* Avatar showcase */}
        <div className="relative rounded-2xl overflow-hidden border border-amber-500/30 mb-3 aspect-video bg-gradient-to-br from-indigo-700 via-purple-800 to-slate-950">
          {(avatar?.image_url || avatar?.avatar_url) ? (
            <img src={avatar.image_url || avatar.avatar_url} alt={avatar?.display_name}
              className="w-full h-full object-cover"
              style={{ filter: busy ? 'brightness(0.9) saturate(1.2)' : 'brightness(1) saturate(1.1)' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-[10rem] font-black text-white/90 drop-shadow-2xl anant-float">
                {avatar?.display_name?.[0]}
              </div>
            </div>
          )}
          {busy && (
            <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full bg-black/70 text-amber-300 text-xs font-bold inline-flex items-center gap-2">
              <Volume2 className="w-3 h-3 anant-pulse-soft" /> {avatar?.display_name?.split(' ')[0]} is speaking…
            </div>
          )}
          <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-rose-500 text-white text-[10px] font-bold inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white anant-pulse-soft" /> LIVE
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-2 mb-3 max-h-[40vh]" data-testid="sankat-paid-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-100'}`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2">
          <button onClick={toggleListen} data-testid="sankat-paid-mic-btn"
            className={`w-12 h-12 rounded-full flex items-center justify-center border ${listening ? 'bg-rose-500 text-white anant-pulse-soft' : 'bg-slate-800 text-amber-300 border-slate-700 hover:bg-slate-700'}`}>
            <Mic className="w-5 h-5" />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Apni samasya likhiye…"
            data-testid="sankat-paid-input"
            className="flex-1 px-4 py-3 rounded-full bg-slate-900 border border-slate-700 text-slate-100 focus:border-amber-400 focus:outline-none"
            disabled={busy}
          />
          <button onClick={send} disabled={!input.trim() || busy} data-testid="sankat-paid-send-btn"
            className="w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center disabled:opacity-50">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Expired modal — offer Continue (pay 200 AT more) or End */}
      {showExpiredModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="sankat-expired-modal">
          <div className="max-w-sm w-full rounded-2xl bg-slate-900 border-2 border-amber-400/40 p-5 text-center">
            <div className="text-3xl mb-2">⏰</div>
            <div className="text-amber-300 text-lg font-extrabold mb-1">Session time over</div>
            <div className="text-slate-300 text-sm mb-4">
              Aapka {Math.floor((pricing.duration_sec || 900) / 60)}-min session complete ho gaya. Continue karna hai to sirf <b className="text-amber-200">{pricing.cost_at} AT</b> aur, aur old chat bhi preserved rahega. Warna End.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => endSession('expired')} data-testid="sankat-expired-end-btn"
                className="py-3 rounded-xl bg-rose-500 hover:bg-rose-400 text-white font-bold text-sm">End session</button>
              <button onClick={continueSession} disabled={busy} data-testid="sankat-expired-continue-btn"
                className="py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm disabled:opacity-60">
                +{Math.floor((pricing.duration_sec || 900) / 60)}m · Pay {pricing.cost_at} AT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SankatPaidSession;
